import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import GenieModal from "@/components/GenieModal";

const routeContext = (path) => {
  if (path.startsWith("/organiser") || path.startsWith("/organizer")) return "organisers";
  if (path.startsWith("/brands")) return "brands";
  if (path.startsWith("/ecosystem") || path.startsWith("/work-with-eventom")) return "ecosystem";
  return "fans";
};

/** Small floating Genie entry point — present across the whole app. */
export default function GenieLauncher() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  if (pathname.startsWith("/chat") || pathname.startsWith("/admin") || pathname.startsWith("/scanner")) return null;
  const context = routeContext(pathname);
  const tagline = {
    fans: "Plan tonight in 10 seconds",
    organisers: "Your AI co-organiser",
    brands: "Match to the right event",
    ecosystem: "Find your next gig",
  }[context];

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="genie-launcher"
        className="fixed z-[70] right-4 bottom-24 md:bottom-6 flex items-center gap-2 pl-1.5 pr-4 py-1.5 rounded-full transition-all active:scale-95 hover:-translate-y-0.5 genie-glow group"
        style={{ background: "#171717", border: "1px solid #2A2A2A" }}
        aria-label="Ask Eventom Genie">
        <span className="relative block w-9 h-9">
          <img src="/genie-bust.png" alt="" className="w-9 h-9 object-contain genie-float" data-testid="genie-art-launcher" />
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-lime shadow-[0_0_8px_#AAEE00] animate-pulse" />
        </span>
        <span className="text-left leading-tight">
          <span className="block font-heading font-bold text-xs text-white">Ask Genie</span>
          <span className="hidden sm:block text-[9px] font-mono uppercase tracking-widest text-lime/85 -mt-0.5">{tagline}</span>
        </span>
      </button>
      <GenieModal open={open} onClose={() => setOpen(false)} context={context} />
      <style>{`
        @keyframes genieFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
        @keyframes genieAura { 0%,100% { opacity: 0.6; } 50% { opacity: 1; } }
      `}</style>
    </>
  );
}

/** Inline Genie card — drop into any page section as a natural entry point. */
export function GenieCard({ context = "fans", title = "Eventom Genie", sub = "Your AI for Events & Experiences", testId = "genie-card" }) {
  const [open, setOpen] = useState(false);
  const bullets = {
    fans: ["Find events tonight", "Compare venues", "Recommend by mood"],
    organisers: ["Draft your event", "Find artists", "Fix ticket sales"],
    brands: ["Match sponsorships", "Audience insights", "Plan activations"],
    ecosystem: ["Find gigs", "Match brands", "Grow bookings"],
  }[context] || ["Ask anything", "Get real answers", "Skip the search"];
  return (
    <>
      <div className="rounded-2xl p-4 flex items-center gap-3 genie-glow relative overflow-hidden" data-testid={testId}
        style={{ background: "#171717", border: "1px solid #2A2A2A" }}>
        <span className="pointer-events-none absolute -right-8 -top-8 w-32 h-32 rounded-full bg-lime/10 blur-2xl" />
        <span className="pointer-events-none absolute -left-6 -bottom-6 w-28 h-28 rounded-full bg-magenta/10 blur-2xl" />
        <span className="relative block w-12 h-12 shrink-0">
          <img src="/genie-bust.png" alt="Eventom Genie" className="w-12 h-12 object-contain" data-testid="genie-art-card" />
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-lime shadow-[0_0_8px_#AAEE00] animate-pulse" />
        </span>
        <div className="flex-1 min-w-0 relative">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-heading font-bold text-sm text-white">{title}</div>
            <span className="chip !text-[9px] !text-lime !border-lime/40 !bg-lime/10 !py-0.5 !px-1.5">Live · Free</span>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-skyx/80 truncate">{sub}</div>
          <div className="hidden sm:flex flex-wrap gap-1 mt-1.5">
            {bullets.map((b) => (
              <span key={b} className="text-[9px] font-mono uppercase tracking-widest text-white/60 px-1.5 py-0.5 rounded-full border border-white/10 bg-white/5">{b}</span>
            ))}
          </div>
        </div>
        <button onClick={() => setOpen(true)} data-testid={`${testId}-open`} className="btn-orange !py-2 !px-4 !text-xs shrink-0 relative">Ask Genie</button>
      </div>
      <GenieModal open={open} onClose={() => setOpen(false)} context={context} />
    </>
  );
}
